'use client';

import Icon from '@/components/ui/AppIcon';
import Footer from '@/components/common/Footer';
import Link from 'next/link';
import Navbar from '@/components/common/Navbar';

export default function PrivacyPolicyPage() {
  return (
    <main className="min-h-screen bg-background">
      <Navbar />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-green-600 to-green-800 text-white py-20">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <Link href="/homepage" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors">
              <Icon name="ArrowLeftIcon" size={20} />
              <span>Back to Home</span>
            </Link>
            <h1 className="text-5xl lg:text-6xl font-bold mb-6">Privacy Policy</h1>
            <p className="text-xl text-white/90">Last updated: February 19, 2026</p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-20">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg p-8 lg:p-12">
            <div className="prose prose-lg max-w-none">
              <h2 className="text-3xl font-bold text-foreground mb-6">Introduction</h2>
              <p className="text-muted-foreground mb-6">
                Gelle Cleaning LLC ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website and services.
              </p>

              <h2 className="text-3xl font-bold text-foreground mb-6 mt-12">Information We Collect</h2>
              <h3 className="text-2xl font-semibold text-foreground mb-4">Personal Information</h3>
              <p className="text-muted-foreground mb-4">
                We collect personal information that you voluntarily provide to us when you:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>Book a cleaning service</li>
                <li>Create an account</li>
                <li>Contact us through our contact form</li>
                <li>Subscribe to our newsletter</li>
                <li>Participate in our referral program</li>
              </ul>
              <p className="text-muted-foreground mb-6">
                This information may include: name, email address, phone number, service address, billing information, and payment details.
              </p>

              <h3 className="text-2xl font-semibold text-foreground mb-4">Automatically Collected Information</h3>
              <p className="text-muted-foreground mb-6">
                When you visit our website, we automatically collect certain information about your device, including information about your web browser, IP address, time zone, and some of the cookies installed on your device.
              </p>

              <h2 className="text-3xl font-bold text-foreground mb-6 mt-12">How We Use Your Information</h2>
              <p className="text-muted-foreground mb-4">
                We use the information we collect to:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>Provide, operate, and maintain our cleaning services</li>
                <li>Process your bookings and payments</li>
                <li>Send you booking confirmations and service reminders</li>
                <li>Respond to your inquiries and provide customer support</li>
                <li>Send you marketing communications (with your consent)</li>
                <li>Improve our website and services</li>
                <li>Prevent fraud and enhance security</li>
              </ul>

              <h2 className="text-3xl font-bold text-foreground mb-6 mt-12">Information Sharing</h2>
              <p className="text-muted-foreground mb-6">
                We do not sell, trade, or rent your personal information to third parties. We may share your information with:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li><strong>Service Providers:</strong> Third-party companies that help us operate our business (payment processors, email services)</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                <li><strong>Business Transfers:</strong> In connection with a merger, sale, or acquisition</li>
              </ul>

              <h2 className="text-3xl font-bold text-foreground mb-6 mt-12">Data Security</h2>
              <p className="text-muted-foreground mb-6">
                We implement appropriate technical and organizational security measures to protect your personal information. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
              </p>

              <h2 className="text-3xl font-bold text-foreground mb-6 mt-12">Your Rights</h2>
              <p className="text-muted-foreground mb-4">
                You have the right to:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                <li>Access the personal information we hold about you</li>
                <li>Request correction of inaccurate information</li>
                <li>Request deletion of your personal information</li>
                <li>Opt-out of marketing communications</li>
                <li>Object to processing of your personal information</li>
              </ul>

              <h2 className="text-3xl font-bold text-foreground mb-6 mt-12">Cookies</h2>
              <p className="text-muted-foreground mb-6">
                We use cookies and similar tracking technologies to track activity on our website and store certain information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.
              </p>

              <h2 className="text-3xl font-bold text-foreground mb-6 mt-12">Children's Privacy</h2>
              <p className="text-muted-foreground mb-6">
                Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children under 18.
              </p>

              <h2 className="text-3xl font-bold text-foreground mb-6 mt-12">Changes to This Policy</h2>
              <p className="text-muted-foreground mb-6">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
              </p>

              <h2 className="text-3xl font-bold text-foreground mb-6 mt-12">Contact Us</h2>
              <p className="text-muted-foreground mb-4">
                If you have any questions about this Privacy Policy, please contact us:
              </p>
              <div className="bg-green-50 p-6 rounded-lg">
                <p className="text-foreground mb-2"><strong>Email:</strong> privacy@gellecleaning.com</p>
                <p className="text-foreground mb-2"><strong>Phone:</strong> (612) 555-1234</p>
                <p className="text-foreground"><strong>Address:</strong> Twin Cities, Minneapolis, MN</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}